import { createContext, useContext } from "react";
import { StyleProp, TextStyle, ViewStyle } from "react-native";
import { Color } from "react-native-svg";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

export type tPdButtonColorScheme = "green" | "purple" | "pink" | "red" | "white";

export type tPdButtonSize = "lg" | "md" | "sm";

export type tPdButtonVariant = "solid" | "ghost" | "outline" | "link";

interface iButtonContext {
  colorScheme: tPdButtonColorScheme;
  size: tPdButtonSize;
  variant: tPdButtonVariant;
  pressed: boolean;
  hovered?: boolean;
  focused?: boolean;
}

export const ButtonContext = createContext<Partial<iButtonContext>>({});

const getColorFromScheme = (colorScheme?: tPdButtonColorScheme, reverse?: boolean) => {
  if (colorScheme === "purple") {
    return reverse ? kColors.white : kColors.brandPurple;
  } else if (colorScheme === "pink") {
    return reverse ? kColors.brandDarkGreen : kColors.brandPink2;
  } else if (colorScheme === "red") {
    return reverse ? kColors.white : kColors.secondaryRed;
  } else if (colorScheme === "white") {
    return reverse ? kColors.brandDarkGreen : kColors.white;
  }
  return reverse ? kColors.white : kColors.brandDarkGreen;
};

export const useButtonStyles = (isDisabled?: boolean): ViewStyle => {
  const { colorScheme, size, variant, hovered, pressed } = useContext(ButtonContext);
  const color = getColorFromScheme(colorScheme, false);

  const styles: StyleProp<ViewStyle> = {
    height: 40,
    paddingHorizontal: kStyleConst.kSpacing * 6,
    backgroundColor: color,
    borderColor: color,
  };

  if (size === "sm") {
    styles.height = 32;
    styles.paddingHorizontal = kStyleConst.kSpacing * 4;
  }

  if (size === "lg" && variant !== "link") {
    styles.height = 56;
    styles.paddingHorizontal = kStyleConst.kSpacing * 8;
  }

  if (variant === "outline" || hovered) {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = color;
  }

  if (variant === "link") {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = color;
    styles.height = 30;
    styles.paddingHorizontal = 0;
    // styles.paddingRight = kStyleConst.kSpacing * 6;
    styles.borderWidth = 0;
    styles.borderBottomWidth = 2;
    styles.paddingLeft = 0;
    styles.justifyContent = "flex-start";
  }

  if (variant === "ghost") {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = kColors.transparent;
  }

  if (isDisabled || pressed) {
    styles.opacity = 0.6;
  }

  return styles;
};

export const useIconButtonStyles = (isDisabled?: boolean): ViewStyle => {
  const { colorScheme, size, variant, hovered, pressed } = useContext(ButtonContext);
  const color = getColorFromScheme(colorScheme, false);

  const styles: StyleProp<ViewStyle> = {
    height: 40,
    width: 40,
    // padding: kStyleConst.kSpacing * 3,
    backgroundColor: color,
    borderColor: color,
  };

  if (size === "sm") {
    styles.height = 32;
    styles.width = 32;
    // styles.padding = kStyleConst.kSpacing;
  }

  if (size === "lg") {
    styles.height = 56;
    styles.width = 56;
    // styles.padding = kStyleConst.kSpacing * 4;
  }

  if (variant === "outline" || hovered) {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = color;
  }

  if (variant === "ghost") {
    styles.backgroundColor = kColors.transparent;
    styles.borderColor = kColors.transparent;
  }

  if (isDisabled || pressed) {
    styles.opacity = 0.6;
  }

  return styles;
};

export const useButtonLabelStyles = (): TextStyle & { color?: Color } => {
  const { colorScheme, size, variant, hovered } = useContext(ButtonContext);

  const styles = {
    color: getColorFromScheme(colorScheme, variant === "solid" && !hovered),
    fontSize: 10,
  };

  if (size === "lg") {
    styles.fontSize = 12;
  }

  return styles;
};
